import * as stylex from '@stylexjs/stylex'
import { useId, type ReactNode } from 'react'
import { Box } from '../Box/Box'
import { HelpText, type HelpTextProps } from '../HelpText/HelpText'
import { HStack } from '../HStack/HStack'
import { InputBase, type InputBaseProps } from '../InputBase/InputBase'
import { Stack } from '../Stack/Stack'
import { Text } from '../Text/Text'
import { colors } from '../tokens/colors.stylex'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'

const variantStyles = stylex.create({
  outlined: {
    borderColor: {
      'default': colors.grey300,
      ':focus-within': colors.grey800,
      '@media (hover: hover)': {
        'default': null,
        ':hover:not(:disabled)': colors.grey800,
      },
    },
  },
  outlinedDisabled: {
    borderColor: colors.grey300,
    backgroundColor: colors.grey200,
  },
  outlinedError: {
    borderColor: colors.red600,
  },
  filled: {
    borderColor: 'transparent',
    backgroundColor: colors.grey100,
  },
  filledDisabled: {
    backgroundColor: colors.grey200,
  },
  filledError: {
    borderColor: colors.red600,
  },
  width: (value: string | number) => ({ width: value }),
  minWidth: (value: string | number) => ({ minWidth: value }),
})

const _baseStyles = stylex.create({
  root: {
    display: 'inline-flex',
    flexDirection: 'column',
    position: 'relative',
  },
  label: {
    color: colors.grey800,
  },
  fullWidth: {
    width: '100%',
  },
  helpText: {
    marginTop: 4,
  },
  input: {
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
    transition: '200ms cubic-bezier(0, 0, 0.2, 1) 0ms',
  },
  inputSmall: {
    padding: '5px 8px',
  },
  inputMedium: {
    padding: '7px 8px',
  },
  container: {
    gap: 4,
    display: 'flex',
    flexDirection: 'column',
  },
})

export type InputProps<Multiline extends boolean = false> = Omit<InputBaseProps<Multiline>, 'xStyles' | 'rootStyle' | 'size'> & {
  /**
   * 輸入框上方的標籤文字
   */
  label?: ReactNode
  /**
   * 是否顯示錯誤狀態（紅色邊框）
   */
  error?: boolean
  /**
   * 輸入框下方的說明或錯誤訊息
   */
  helpText?: ReactNode
  /**
   * 傳遞給 HelpText 元件的額外 props
   */
  HelpTextProps?: Omit<HelpTextProps, 'children'>
  /**
   * 輸入框樣式變體
   * @default 'outlined'
   */
  variant?: 'outlined' | 'filled'
  /**
   * 自訂根元素樣式（StyleX）
   */
  rootStyle?: stylex.StyleXStyles
  /**
   * 覆寫內部子元素樣式，base 用於外層容器，inputBase 用於內層輸入元件
   */
  xStyles?: {
    base?: Partial<XStyles<keyof typeof _baseStyles>>
    inputBase?: InputBaseProps['xStyles']
  }
  /**
   * 輸入框寬度
   */
  width?: string | number
  /**
   * 輸入框最小寬度
   */
  minWidth?: string | number
  /** 控制輸入框高度；small 為 32px，medium 為 36px。 */
  size?: 'small' | 'medium'
  /**
   * 原生 input 的 size 屬性（字元數）。
   * 用來調整輸入框的固有寬度——外層若靠內容決定寬度（shrink-to-fit），
   * 這個值會構成寬度下限；預設 20 換算約 154px。
   * textarea 沒有這個屬性，multiline 模式下不會生效也不會往下傳。
   */
  inputSize?: number
}

/**
 * Input 用於接收使用者的文字輸入，內建標籤、錯誤狀態與說明文字。
 * 支援 outlined / filled 兩種變體，以及 multiline（textarea）模式。
 * 底層封裝 InputBase，提供更完整的表單輸入體驗。
 *
 * @example
 * ```tsx
 * <Input label="名稱" placeholder="請輸入" />
 * <Input label="備註" multiline rows={4} />
 * <Input label="信箱" error helpText="格式不正確" />
 * ```
 */
export const Input = <Multiline extends boolean = false>(props: InputProps<Multiline>) => {
  const { rootStyle, xStyles, disabled, error, label, fullWidth, helpText, HelpTextProps, width, variant = 'outlined', minWidth, required, id: idProp, size = 'medium', inputSize, ...rest } = props
  const baseStyles = useXStyles(_baseStyles, xStyles?.base)
  const generatedId = useId()
  const inputId = idProp ?? generatedId

  return (
    <Box
      rootStyle={[
        baseStyles.root,
        fullWidth && baseStyles.fullWidth,
        rootStyle
      ]}
      width={width}
    >
      <Stack rootStyle={baseStyles.container}>
        {label && (
          <HStack>
            <Text component="label" htmlFor={inputId} variant="body" rootStyle={[baseStyles.label]}>
              {label}
            </Text>
            {required && <Text color={colors.red600}> *</Text>}
          </HStack>
        )}
        <InputBase
          id={inputId}
          disabled={disabled}
          fullWidth={fullWidth}
          size={props.multiline ? undefined : inputSize}
          xStyles={{
            ...xStyles?.inputBase,
            root: [
              baseStyles.input,
              size === 'small' ? baseStyles.inputSmall : baseStyles.inputMedium,
              variantStyles[variant],
              disabled && variantStyles[`${variant}Disabled`],
              error && variantStyles[`${variant}Error`],
              xStyles?.inputBase?.root,
              !!width && variantStyles.width(width),
              !!minWidth && variantStyles.minWidth(minWidth),
            ],
          }}
          {...(rest as InputBaseProps)}
        />
      </Stack>
      {helpText
      && (
        <HelpText
          {...HelpTextProps}
          error={error}
          rootStyle={[!HelpTextProps?.floating && baseStyles?.helpText]}
        >
          {helpText}
        </HelpText>
      )}
    </Box>
  )
}
